import React from 'react'
import LinearProgress from '@material-ui/core/LinearProgress'
import { apiClient } from '../../Module/API/Client'
import { updateMissionSimplified } from '../../Module/graphQL'
import {
  MISSION_STATUS_ENUM,
  STATUS_AVAILABLE,
  STATUS_CAMPAIGN_ENUM_ACTIVE,
} from '../../shared/constants'

// These bulk actions run over thousands of missions a few dozen at a time, so
// they can take minutes. The dialog stays on screen for the whole run, so it
// has to say how far along it is — an unchanging "are you sure?" box reads as
// a hang, and ops start clicking things again.
export const BULK_BATCH_SIZE = 25

export const bulkProgressBody = ({ done, total, verb }) => {
  const percent = total ? Math.round((done / total) * 100) : 0

  return (
    <section className="redux-modal">
      <article>
        <p>
          {verb} <strong>{done.toLocaleString()}</strong> of{' '}
          <strong>{total.toLocaleString()}</strong> missions ({percent}%).
        </p>
        <LinearProgress variant="determinate" value={percent} />
        <p>
          <small>
            This can take a few minutes for large city plans. Leaving this page
            will stop it part-way.
          </small>
        </p>
      </article>
    </section>
  )
}

/**
 * Replaces the open confirmation dialog with a progress one. The buttons are
 * hidden so the action cannot be fired a second time while it is still running.
 *
 * @param {function} showReduxModal
 * @param {object} progress `{ done, total, verb }`
 * @param {string} title
 */
export const showBulkProgress = (showReduxModal, progress, title) =>
  showReduxModal({
    title,
    children: bulkProgressBody(progress),
    hideButtons: true,
    hideCancel: true,
  })

export const confirmationModalBody = ({
  isTeamLeaderEnabled = false,
  missionsCount = 0,
}) => {
  return (
    <section className="redux-modal">
      {isTeamLeaderEnabled ? (
        <article>
          <p>
            You are publishing {missionsCount} missions. Here's how they'll be
            visible:
          </p>
          <br />
          <ul>
            <li>
              <strong>Team Leaders First:</strong> If the rating feature is
              enabled in your country, the remaining missions will be released
              to other BAs over the next 90 minutes.
            </li>
            <li>
              <strong>Gradual Release:</strong> TLs will be able to view and
              assign missions to their teams.
            </li>
            <li>
              <strong>Rating Priority:</strong> BAs with higher ratings will see
              missions earlier.
            </li>
          </ul>
        </article>
      ) : (
        <article>
          <p>
            If rating features are enabled for your country, missions will
            gradually become available over the next 90 minutes, with the
            highest-rated brand ambassadors seeing them first.
          </p>
        </article>
      )}
    </section>
  )
}

export const getShowDeleteAllNonStartedModalText = (
  isAnyBooked,
  missionsSpyCheckedInIds,
) =>
  isAnyBooked ? (
    <div>
      {missionsSpyCheckedInIds.length > 0 ? (
        <p>
          These missions can not be deleted because spy already checked in:{' '}
          {missionsSpyCheckedInIds.map((m, i) => {
            const divider = i === missionsSpyCheckedInIds.length - 1 ? '.' : ','
            return (
              <span key={m.id}>
                <a
                  target="_blank"
                  href={'/dashboard/mission/' + m.id}
                  rel="noreferrer"
                >
                  {m.id}
                </a>
                {divider}{' '}
              </span>
            )
          })}
        </p>
      ) : (
        ''
      )}
      {missionsSpyCheckedInIds.length > 0 ? <br /> : ''}
      <p>
        All BAs and Spies who have booked missions will be un-booked and will a
        receive notification.
      </p>
      <p>All available and draft missions will then be permanently deleted.</p>
      <p>
        Please select a reason to be displayed to BAs who are being unbooked.
      </p>
    </div>
  ) : (
    'All available and draft missions will be permanently deleted'
  )

export const onPublishDraftMissions = ({
  campaign,
  missions,
  showReduxModal,
  updateData,
  isTeamLeaderEnabled,
  runSnackBar,
  hideReduxModal,
}) => {
  if (campaign.status != STATUS_CAMPAIGN_ENUM_ACTIVE) {
    return showReduxModal({
      title: 'Draft campaign',
      children:
        'Missions are allowed to be published only if campaign is active.',
      confirmLabel: 'OK',
      hideCancel: true,
    })
  }

  const draftMissions = missions.filter(
    mission => mission.status == MISSION_STATUS_ENUM.DRAFT,
  )
  const totalMissionsToPublish = draftMissions.length

  if (totalMissionsToPublish < 1) {
    return showReduxModal({
      title: 'No missions to publish',
      confirmLabel: 'OK',
      hideCancel: true,
    })
  }

  const missionBatches = []

  for (let i = 0; i < draftMissions.length; i += BULK_BATCH_SIZE) {
    missionBatches.push(draftMissions.slice(i, i + BULK_BATCH_SIZE))
  }

  showReduxModal({
    title: `Publishing ${totalMissionsToPublish} Missions`,
    onConfirm: async () => {
      showBulkProgress(
        showReduxModal,
        { done: 0, total: totalMissionsToPublish, verb: 'Published' },
        'Publishing missions',
      )
      const missionUpdates = draftMissions.map(m => ({
        id: m.id,
        status: STATUS_AVAILABLE,
      }))
      const updatedMissionsIds = missionUpdates.map(({ id }) => id)

      try {
        let hasError = false
        let errorMessage = null
        let done = 0

        for (let i = 0; i < missionBatches.length && !hasError; i++) {
          const batchUpdates = missionBatches[i].map(m => ({
            id: m.id,
            status: STATUS_AVAILABLE,
          }))

          const req = await apiClient.publishMissions(batchUpdates)
          const res = await req.json()

          if (res.message) {
            hasError = true
            errorMessage = res.message
            break
          }

          done += batchUpdates.length
          showBulkProgress(
            showReduxModal,
            { done, total: totalMissionsToPublish, verb: 'Published' },
            'Publishing missions',
          )
        }

        if (hasError) {
          hideReduxModal()
          setTimeout(() => {
            showReduxModal({
              title: 'Cannot publish',
              children: errorMessage,
              confirmLabel: 'OK',
              hideCancel: true,
            })
            runSnackBar({
              type: 'error',
              msg: errorMessage,
            })
          }, 200)
        } else {
          updateData(missions =>
            missions.map(mission =>
              updatedMissionsIds.includes(mission.id)
                ? { ...mission, status: MISSION_STATUS_ENUM.AVAILABLE }
                : mission,
            ),
          )
          runSnackBar({
            type: 'success',
            msg: `${totalMissionsToPublish.toLocaleString()} missions published`,
          })
        }
      } catch (error) {
        updateData(missions =>
          missions.map(mission =>
            updatedMissionsIds.includes(mission.id)
              ? { ...mission, status: MISSION_STATUS_ENUM.DRAFT }
              : mission,
          ),
        )
        hideReduxModal()
        const errorMessage = error.message || 'An unexpected error occurred'
        setTimeout(() => {
          runSnackBar({
            type: 'error',
            msg: errorMessage,
          })

          showReduxModal({
            title: 'Error publishing missions',
            children: errorMessage,
            confirmLabel: 'OK',
            hideCancel: true,
          })
        }, 200)
      }
    },
    children: confirmationModalBody({
      isTeamLeaderEnabled,
      missionsCount: totalMissionsToPublish,
    }),
    confirmLabel: `Publish ${totalMissionsToPublish} missions`,
  })
}

export const readyToPublishModalBody = ({
  isReadyToPublish,
  missionsCount = 0,
}) => (
  <section className="redux-modal">
    <article>
      {isReadyToPublish ? (
        <p>
          {missionsCount} draft missions will be marked as ready to publish.
          They stay drafts and are not visible to brand ambassadors until they
          are published.
        </p>
      ) : (
        <p>
          {missionsCount} draft missions will no longer be marked as ready to
          publish.
        </p>
      )}
    </article>
  </section>
)

/**
 * Marks or unmarks draft missions as ready to publish. The mission status is
 * not touched, only the `isReadyToPublish` flag.
 * @param {object} params
 * @param {Array<object>} params.missions The missions to consider
 * @param {boolean} params.isReadyToPublish The value to set the flag to
 */
export const onSetDraftMissionsReadyToPublish = ({
  missions,
  isReadyToPublish,
  showReduxModal,
  updateData,
  runSnackBar,
  hideReduxModal,
}) => {
  const actionLabel = isReadyToPublish ? 'Mark' : 'Unmark'

  const missionsToUpdate = missions.filter(
    mission =>
      mission.status == MISSION_STATUS_ENUM.DRAFT &&
      Boolean(mission.isReadyToPublish) !== isReadyToPublish,
  )
  const totalMissionsToUpdate = missionsToUpdate.length

  if (totalMissionsToUpdate < 1) {
    return showReduxModal({
      title: `No draft missions to ${actionLabel.toLowerCase()}`,
      confirmLabel: 'OK',
      hideCancel: true,
    })
  }

  const missionBatches = []

  for (let i = 0; i < missionsToUpdate.length; i += BULK_BATCH_SIZE) {
    missionBatches.push(missionsToUpdate.slice(i, i + BULK_BATCH_SIZE))
  }

  const updatedMissionsIds = missionsToUpdate.map(({ id }) => id)
  const progressTitle = `${actionLabel}ing missions`

  showReduxModal({
    title: `${actionLabel} ${totalMissionsToUpdate} draft missions ready to publish`,
    onConfirm: async () => {
      showBulkProgress(
        showReduxModal,
        { done: 0, total: totalMissionsToUpdate, verb: `${actionLabel}ed` },
        progressTitle,
      )

      try {
        let errorMessage = null
        let done = 0

        for (let i = 0; i < missionBatches.length && !errorMessage; i++) {
          const responses = await Promise.all(
            missionBatches[i].map(m =>
              updateMissionSimplified(
                { missionInput: { id: m.id, isReadyToPublish } },
                { ignoreErrors: true },
              ),
            ),
          )
          const results = await Promise.all(responses.map(res => res.json()))
          const failed = results.find(
            result => result.errors && result.errors.length,
          )

          if (failed) {
            errorMessage = failed.errors[0].message
          } else {
            done += missionBatches[i].length
            showBulkProgress(
              showReduxModal,
              {
                done,
                total: totalMissionsToUpdate,
                verb: `${actionLabel}ed`,
              },
              progressTitle,
            )
          }
        }

        if (errorMessage) {
          hideReduxModal()
          setTimeout(() => {
            showReduxModal({
              title: `Cannot ${actionLabel.toLowerCase()}`,
              children: errorMessage,
              confirmLabel: 'OK',
              hideCancel: true,
            })
            runSnackBar({
              type: 'error',
              msg: errorMessage,
            })
          }, 200)
        } else {
          updateData(missions =>
            missions.map(mission =>
              updatedMissionsIds.includes(mission.id)
                ? { ...mission, isReadyToPublish }
                : mission,
            ),
          )
          runSnackBar({
            type: 'success',
            msg: `${totalMissionsToUpdate.toLocaleString()} missions ${actionLabel.toLowerCase()}ed as ready to publish`,
          })
        }
      } catch (error) {
        updateData(missions =>
          missions.map(mission =>
            updatedMissionsIds.includes(mission.id)
              ? { ...mission, isReadyToPublish: !isReadyToPublish }
              : mission,
          ),
        )
        hideReduxModal()
        const errorMessage = error.message || 'An unexpected error occurred'
        setTimeout(() => {
          runSnackBar({
            type: 'error',
            msg: errorMessage,
          })

          showReduxModal({
            title: `Error ${actionLabel.toLowerCase()}ing missions`,
            children: errorMessage,
            confirmLabel: 'OK',
            hideCancel: true,
          })
        }, 200)
      }
    },
    children: readyToPublishModalBody({
      isReadyToPublish,
      missionsCount: totalMissionsToUpdate,
    }),
    confirmLabel: `${actionLabel} ${totalMissionsToUpdate} missions`,
  })
}

// ---- Server-side bulk operations (letterbox city plans) --------------------

const JOB_POLL_MS = 2500
const JOB_TERMINAL = ['DONE', 'FAILED']

/**
 * The outcome of a bulk mutation as one readable line: how many changed and,
 * grouped by reason, how many were refused.
 * @param {{matchedCount: number, updatedCount: number, skipped: Array<{reason: string}>}} result
 * @param {string} verb Past tense, e.g. "Published"
 * @returns {string}
 */
export const describeBulkResult = (result, verb) => {
  const reasons = (result.skipped || []).reduce((acc, { reason }) => {
    acc[reason] = (acc[reason] || 0) + 1
    return acc
  }, {})
  const skipped = Object.entries(reasons)
    .map(
      ([reason, count]) =>
        `${count.toLocaleString()} ${labelForSkipReason(reason)}`,
    )
    .join(', ')
  const base = `${verb} ${result.updatedCount.toLocaleString()} of ${result.matchedCount.toLocaleString()} missions`
  return skipped ? `${base} (skipped: ${skipped})` : `${base}.`
}

const SKIP_REASON_LABELS = {
  NOT_DRAFT: 'already published',
  LOCKED_PAID: 'already paid',
  HAS_SPY_LINK: 'with a spy booked',
  PAY_ADJUSTMENT_OUT_OF_BOUNDS: 'with a pay adjustment out of bounds',
  DATE_IN_PAST: 'in the past',
  DATE_TOO_FAR: 'more than six weeks away',
  MISSING_PUBLISH_FIELDS: 'missing publish settings',
  NO_BOUNDARY: 'without an area',
}

export const labelForSkipReason = reason => SKIP_REASON_LABELS[reason] || reason

/**
 * Runs a server-side bulk operation with the same progress dialog the batched
 * actions use. `start` performs the mutation and resolves to a
 * BulkMissionsResult; when that result carries a `job`, the dialog stays up
 * and polls `fetchJob(id)` until the job is DONE or FAILED. `onDone` gets the
 * final result (the job's result when it ran in the background).
 *
 * @param {object} args
 * @param {function} args.showReduxModal
 * @param {function} args.hideReduxModal
 * @param {string} args.title Dialog title while running
 * @param {string} args.verb Past tense for the progress line
 * @param {number} args.total How many missions the selection holds
 * @param {function} args.start () => Promise<BulkMissionsResult>
 * @param {function} args.fetchJob (id) => Promise<MissionBulkJob>
 * @param {function} args.onDone (result) => void
 * @returns {Promise<?object>} the final result, or null on failure
 */
export const runBulkJob = async ({
  showReduxModal,
  hideReduxModal,
  title,
  verb,
  total,
  start,
  fetchJob,
  onDone,
}) => {
  showBulkProgress(showReduxModal, { done: 0, total, verb }, title)

  let result
  try {
    result = await start()
  } catch (error) {
    showReduxModal({
      title: `${title} failed`,
      children: error.message || 'Something went wrong, please try again.',
      confirmLabel: 'OK',
      hideCancel: true,
    })
    return null
  }

  if (result.job) {
    let job = result.job
    while (!JOB_TERMINAL.includes(job.status)) {
      showBulkProgress(
        showReduxModal,
        {
          done: job.processed || 0,
          total: job.total || total,
          verb:
            job.status === 'QUEUED' ? `Queued, ${verb.toLowerCase()}` : verb,
        },
        title,
      )
      await new Promise(resolve => setTimeout(resolve, JOB_POLL_MS))
      try {
        job = await fetchJob(job.id)
      } catch (error) {
        // A transient poll failure is not a job failure; keep polling.
      }
    }
    if (job.status === 'FAILED') {
      showReduxModal({
        title: `${title} failed`,
        children: job.error || 'The background job failed.',
        confirmLabel: 'OK',
        hideCancel: true,
      })
      return null
    }
    result = job.result || {
      matchedCount: total,
      updatedCount: job.processed || 0,
      skipped: [],
    }
  }

  hideReduxModal()
  onDone(result)
  return result
}
